import { InfoOutlineIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Tooltip,
} from "@chakra-ui/react";

import { CloseIcon, PencilIcon } from "@/assets/icons";
import { useColor } from "@/styles/useColor";

interface SlippageControlsProps {
  selectedSlippage: "auto" | "0.5" | "3" | "custom";
  customSlippage: string;
  showSlippageOptions: boolean;
  defaultSlippage: number;
  showSlippageControls: boolean;
  onSlippageChange: (value: string) => void;
  onToggleSlippageOptions: () => void;
}

export const SlippageControls = ({
  selectedSlippage,
  customSlippage,
  showSlippageOptions,
  defaultSlippage,
  showSlippageControls,
  onSlippageChange,
  onToggleSlippageOptions,
}: SlippageControlsProps) => {
  const color = useColor();

  return (
    <>
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="center" gap="2">
          <Text color={color("500")} fontWeight="600">
            Max Slippage
          </Text>
          <Tooltip label="Your transaction will revert if the price changes unfavorably by more than this percentage">
            <Box as="span">
              <InfoOutlineIcon width="16px" height="16px" color={color("500")} />
            </Box>
          </Tooltip>
        </Flex>
        <Flex alignItems="center" gap="6px">
          <Text color={color("700")}>
            {selectedSlippage === "auto"
              ? `Auto (${defaultSlippage}%)`
              : `${customSlippage || selectedSlippage}%`}
          </Text>
          {showSlippageOptions ? (
            <CloseIcon
              width="18px"
              height="18px"
              color={color("400")}
              aria-label="close"
              onClick={onToggleSlippageOptions}
              role="button"
              style={{ cursor: "pointer" }}
            />
          ) : (
            <PencilIcon
              width="18px"
              height="18px"
              color={color("400")}
              aria-label="edit"
              onClick={onToggleSlippageOptions}
              role="button"
              style={{ cursor: "pointer" }}
            />
          )}
        </Flex>
      </Flex>

      {showSlippageControls && showSlippageOptions && (
        <Flex gap="2" marginTop="3">
          <Button
            width="86px"
            height="34px"
            background={selectedSlippage === "auto" ? color("300") : "white"}
            border="none"
            borderRadius="full"
            _hover={{ bg: selectedSlippage === "auto" ? color("300") : color("200") }}
            onClick={() => onSlippageChange("auto")}
            paddingX="12px"
            size="sm"
          >
            Auto
          </Button>
          <Button
            width="86px"
            height="34px"
            background={selectedSlippage === "0.5" ? color("300") : "white"}
            border="none"
            borderRadius="full"
            _hover={{ bg: selectedSlippage === "0.5" ? color("300") : color("200") }}
            onClick={() => onSlippageChange("0.5")}
            paddingX="12px"
            size="sm"
          >
            0.5%
          </Button>
          <Button
            width="86px"
            height="34px"
            background={selectedSlippage === "3" ? color("300") : "white"}
            border="none"
            borderRadius="full"
            _hover={{ bg: selectedSlippage === "3" ? color("300") : color("200") }}
            onClick={() => onSlippageChange("3")}
            paddingX="12px"
            size="sm"
          >
            3%
          </Button>
          <InputGroup width="100%" height="34px" size="sm">
            <Input
              borderRadius="6px"
              _focus={{ borderColor: color("500") }}
              onChange={e => onSlippageChange(e.target.value)}
              placeholder="Custom"
              value={customSlippage}
            />
            <InputRightElement paddingRight="12px">%</InputRightElement>
          </InputGroup>
        </Flex>
      )}
    </>
  );
};
